import { db } from '../config/firebase';
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { writeAuditLog } from './auditService';

// ─── APP CONFIG (bootstrap / first-run) ───────────────────────────────────────

export async function getSetupConfig() {
  const snap = await getDoc(doc(db, 'app_config', 'setup'));
  return snap.exists() ? snap.data() : { admin_created: false };
}

export async function markSetupComplete() {
  await setDoc(doc(db, 'app_config', 'setup'), {
    admin_created: true,
    completed_at: serverTimestamp(),
  });
}

// ─── USERS ────────────────────────────────────────────────────────────────────

export async function getUserById(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getAllUsers() {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function createUserDocument({ uid, email, fullName, role, fundAccess, streamAccess, createdBy }) {
  const data = {
    id: uid,
    email,
    full_name: fullName,
    display_name: fullName,
    role,
    fund_access: fundAccess || [],
    stream_access: streamAccess || [],
    ic_memberships: [],
    is_active: true,
    created_at: serverTimestamp(),
    created_by: createdBy,
    last_login: null,
  };
  await setDoc(doc(db, 'users', uid), data);
  await writeAuditLog({
    action: 'user_created',
    entityType: 'user',
    entityId: uid,
    performedBy: createdBy,
    details: { email, role },
  });
}

export async function updateUserDocument({ uid, updates, updatedBy }) {
  await updateDoc(doc(db, 'users', uid), updates);
  await writeAuditLog({
    action: 'user_updated',
    entityType: 'user',
    entityId: uid,
    performedBy: updatedBy,
    details: updates,
  });
}

export async function deactivateUserDocument({ uid, deactivatedBy }) {
  await updateDoc(doc(db, 'users', uid), { is_active: false });
  await writeAuditLog({
    action: 'user_deactivated',
    entityType: 'user',
    entityId: uid,
    performedBy: deactivatedBy,
    details: {},
  });
}

// ─── FUNDS ────────────────────────────────────────────────────────────────────

export async function getAllFunds() {
  const snap = await getDocs(collection(db, 'funds'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getFundById(fundId) {
  const snap = await getDoc(doc(db, 'funds', fundId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function saveFund({ fundData, performedBy }) {
  const shortName = (fundData.short_name || '').toLowerCase().replace(/\s+/g, '_');
  const vintage = fundData.vintage_year;
  const fundId = `fund_${shortName}_${vintage}`;

  const existing = await getFundById(fundId);

  if (existing) {
    const previousSnapshot = { ...existing };
    delete previousSnapshot.config_history;

    const historyEntry = {
      version: existing.config_version,
      snapshot: previousSnapshot,
      changed_at: new Date().toISOString(),
      changed_by: performedBy,
    };

    const updatedData = {
      ...fundData,
      id: fundId,
      config_version: (existing.config_version || 1) + 1,
      config_history: [...(existing.config_history || []), historyEntry],
    };

    await setDoc(doc(db, 'funds', fundId), updatedData, { merge: true });
    await writeAuditLog({
      action: 'fund_config_saved',
      entityType: 'fund',
      entityId: fundId,
      performedBy,
      details: { version: updatedData.config_version },
    });
    return fundId;
  }

  const newData = {
    ...fundData,
    id: fundId,
    committed_capital: 0,
    config_version: 1,
    config_history: [],
    is_active: true,
    created_at: serverTimestamp(),
    created_by: performedBy,
  };

  await setDoc(doc(db, 'funds', fundId), newData);
  await writeAuditLog({
    action: 'fund_config_saved',
    entityType: 'fund',
    entityId: fundId,
    performedBy,
    details: { version: 1 },
  });
  return fundId;
}

// ─── STREAMS ──────────────────────────────────────────────────────────────────

export async function getStreamsByFund(fundId) {
  const snap = await getDocs(collection(db, 'funds', fundId, 'streams'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function saveStream({ fundId, streamData, performedBy }) {
  const shortName = (streamData.short_name || '').toLowerCase().replace(/\s+/g, '_');
  const streamId = `stream_${fundId}_${shortName}`;

  const existingSnap = await getDoc(doc(db, 'funds', fundId, 'streams', streamId));
  const existing = existingSnap.exists() ? existingSnap.data() : null;

  if (existing) {
    const historyEntry = {
      version: existing.config_version || 1,
      snapshot: { ...existing },
      changed_at: new Date().toISOString(),
      changed_by: performedBy,
    };
    const updatedData = {
      ...streamData,
      id: streamId,
      fund_id: fundId,
      config_version: (existing.config_version || 1) + 1,
      config_history: [...(existing.config_history || []), historyEntry],
    };
    await setDoc(doc(db, 'funds', fundId, 'streams', streamId), updatedData);
  } else {
    const newData = {
      ...streamData,
      id: streamId,
      fund_id: fundId,
      config_version: 1,
      config_history: [],
      created_at: serverTimestamp(),
      created_by: performedBy,
    };
    await setDoc(doc(db, 'funds', fundId, 'streams', streamId), newData);
  }

  await writeAuditLog({
    action: 'stream_config_saved',
    entityType: 'fund',
    entityId: streamId,
    performedBy,
    details: { fundId, streamId },
  });
  return streamId;
}

// ─── COUNTRIES ────────────────────────────────────────────────────────────────

export async function getAllCountries() {
  const snap = await getDocs(collection(db, 'countries'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function saveCountry({ countryData, performedBy }) {
  const countryId = (countryData.id || countryData.iso_code || '').toUpperCase();
  const data = {
    ...countryData,
    id: countryId,
    added_at: serverTimestamp(),
    added_by: performedBy,
  };
  await setDoc(doc(db, 'countries', countryId), data);
  await writeAuditLog({
    action: 'country_registry_updated',
    entityType: 'system',
    entityId: countryId,
    performedBy,
    details: { name: countryData.name },
  });
  return countryId;
}

export async function updateCountry({ countryId, updates, performedBy }) {
  await updateDoc(doc(db, 'countries', countryId), updates);
  await writeAuditLog({
    action: 'country_registry_updated',
    entityType: 'system',
    entityId: countryId,
    performedBy,
    details: updates,
  });
}

// ─── RUBRICS ──────────────────────────────────────────────────────────────────

export async function getAllRubrics() {
  const snap = await getDocs(collection(db, 'rubrics'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getRubricById(rubricId) {
  const snap = await getDoc(doc(db, 'rubrics', rubricId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function saveRubric({ rubricId, rubricData, performedBy }) {
  if (rubricId) {
    const existing = await getRubricById(rubricId);
    if (existing) {
      const historyEntry = {
        version: existing.version,
        snapshot: { ...existing, version_history: [] },
        changed_at: new Date().toISOString(),
        changed_by: performedBy,
      };
      const updatedData = {
        ...rubricData,
        id: rubricId,
        version: (existing.version || 1) + 1,
        version_history: [...(existing.version_history || []), historyEntry],
      };
      await setDoc(doc(db, 'rubrics', rubricId), updatedData);
      await writeAuditLog({
        action: 'rubric_saved',
        entityType: 'rubric',
        entityId: rubricId,
        performedBy,
        details: { version: updatedData.version },
      });
      return rubricId;
    }
  }

  const newDocRef = rubricId
    ? doc(db, 'rubrics', rubricId)
    : doc(collection(db, 'rubrics'));

  const newData = {
    ...rubricData,
    id: newDocRef.id,
    version: 1,
    version_history: [],
    is_published: false,
    published_at: null,
    published_by: null,
    created_at: serverTimestamp(),
    created_by: performedBy,
  };
  await setDoc(newDocRef, newData);
  await writeAuditLog({
    action: 'rubric_saved',
    entityType: 'rubric',
    entityId: newDocRef.id,
    performedBy,
    details: { version: 1 },
  });
  return newDocRef.id;
}

export async function publishRubric({ rubricId, publishedBy }) {
  const rubricRef = doc(db, 'rubrics', rubricId);
  const rubricSnap = await getDoc(rubricRef);
  if (!rubricSnap.exists()) throw new Error('Rubric not found');
  const { fund_id, stream_id } = rubricSnap.data();

  const q = query(
    collection(db, 'rubrics'),
    where('fund_id', '==', fund_id),
    where('stream_id', '==', stream_id ?? null),
    where('is_published', '==', true),
  );
  const existingSnap = await getDocs(q);

  const batch = writeBatch(db);
  existingSnap.docs.forEach(d => {
    if (d.id !== rubricId) {
      batch.update(d.ref, { is_published: false, unpublished_at: new Date().toISOString() });
    }
  });
  batch.update(rubricRef, {
    is_published: true,
    published_at: serverTimestamp(),
    published_by: publishedBy,
  });
  await batch.commit();

  await writeAuditLog({
    action: 'rubric_published',
    entityType: 'rubric',
    entityId: rubricId,
    performedBy: publishedBy,
    details: {},
  });
}

// ─── DDQ TEMPLATES ────────────────────────────────────────────────────────────

export async function getAllDDQTemplates() {
  const snap = await getDocs(collection(db, 'ddq_templates'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getDDQTemplateById(templateId) {
  const snap = await getDoc(doc(db, 'ddq_templates', templateId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function saveDDQTemplate({ templateId, templateData, performedBy }) {
  if (templateId) {
    const existing = await getDDQTemplateById(templateId);
    if (existing) {
      const historyEntry = {
        version: existing.version,
        snapshot: { ...existing, version_history: [] },
        changed_at: new Date().toISOString(),
        changed_by: performedBy,
      };
      const updatedData = {
        ...templateData,
        id: templateId,
        version: (existing.version || 1) + 1,
        version_history: [...(existing.version_history || []), historyEntry],
      };
      await setDoc(doc(db, 'ddq_templates', templateId), updatedData);
      await writeAuditLog({
        action: 'ddq_template_saved',
        entityType: 'ddq_template',
        entityId: templateId,
        performedBy,
        details: { version: updatedData.version },
      });
      return templateId;
    }
  }

  const newDocRef = doc(collection(db, 'ddq_templates'));
  const newData = {
    status: 'draft',
    ...templateData,
    id: newDocRef.id,
    version: 1,
    version_history: [],
    created_at: serverTimestamp(),
    created_by: performedBy,
  };
  await setDoc(newDocRef, newData);
  await writeAuditLog({
    action: 'ddq_template_saved',
    entityType: 'ddq_template',
    entityId: newDocRef.id,
    performedBy,
    details: { version: 1 },
  });
  return newDocRef.id;
}

// Publishes a template and unpublishes every OTHER published template with the
// same fund_id + stream_id + template_type, so the readers' limit(1) query is
// deterministic. Mirrors publishRubric above.
//
// The scope deliberately includes template_type: publishing a DSQ must not
// unpublish a DDQ (and vice versa) for the same fund + stream. stream_id uses
// ?? null because Firestore's `== null` matches an explicitly-null field only,
// never an absent one.
export async function publishDDQTemplate({ templateId, publishedBy }) {
  const templateRef = doc(db, 'ddq_templates', templateId);
  const templateSnap = await getDoc(templateRef);
  if (!templateSnap.exists()) throw new Error('Template not found');
  const { fund_id, stream_id, template_type } = templateSnap.data();

  const q = query(
    collection(db, 'ddq_templates'),
    where('fund_id', '==', fund_id),
    where('stream_id', '==', stream_id ?? null),
    where('template_type', '==', template_type ?? 'ddq'),
    where('status', '==', 'published'),
  );
  const existingSnap = await getDocs(q);

  const batch = writeBatch(db);
  existingSnap.docs.forEach(d => {
    if (d.id !== templateId) {
      batch.update(d.ref, { status: 'draft', unpublished_at: new Date().toISOString() });
    }
  });
  batch.update(templateRef, {
    status: 'published',
    published_at: serverTimestamp(),
    published_by: publishedBy,
  });
  await batch.commit();

  await writeAuditLog({
    action: 'ddq_template_published',
    entityType: 'ddq_template',
    entityId: templateId,
    performedBy: publishedBy,
    details: { template_type: template_type ?? 'ddq' },
  });
}

export async function unpublishDDQTemplate({ templateId, unpublishedBy }) {
  const templateRef = doc(db, 'ddq_templates', templateId);
  const templateSnap = await getDoc(templateRef);
  if (!templateSnap.exists()) throw new Error('Template not found');

  await updateDoc(templateRef, {
    status: 'draft',
    unpublished_at: new Date().toISOString(),
  });

  await writeAuditLog({
    action: 'ddq_template_unpublished',
    entityType: 'ddq_template',
    entityId: templateId,
    performedBy: unpublishedBy,
    details: {},
  });
}

// ─── APPROVAL SEQUENCES ───────────────────────────────────────────────────────

export async function getAllApprovalSequences() {
  const snap = await getDocs(collection(db, 'approval_sequences'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getApprovalSequenceById(seqId) {
  const snap = await getDoc(doc(db, 'approval_sequences', seqId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function saveApprovalSequence({ seqId, seqData, performedBy }) {
  if (seqId) {
    const existing = await getApprovalSequenceById(seqId);
    if (existing) {
      const historyEntry = {
        version: existing.version,
        snapshot: { ...existing, version_history: [] },
        changed_at: new Date().toISOString(),
        changed_by: performedBy,
      };
      const updatedData = {
        ...seqData,
        id: seqId,
        version: (existing.version || 1) + 1,
        version_history: [...(existing.version_history || []), historyEntry],
      };
      await setDoc(doc(db, 'approval_sequences', seqId), updatedData);
      await writeAuditLog({
        action: 'approval_sequence_saved',
        entityType: 'approval_sequence',
        entityId: seqId,
        performedBy,
        details: { version: updatedData.version },
      });
      return seqId;
    }
  }

  const newDocRef = doc(collection(db, 'approval_sequences'));
  const newData = {
    ...seqData,
    id: newDocRef.id,
    version: 1,
    version_history: [],
    created_at: serverTimestamp(),
    created_by: performedBy,
  };
  await setDoc(newDocRef, newData);
  await writeAuditLog({
    action: 'approval_sequence_saved',
    entityType: 'approval_sequence',
    entityId: newDocRef.id,
    performedBy,
    details: { version: 1 },
  });
  return newDocRef.id;
}

// ─── SLA CONFIG ───────────────────────────────────────────────────────────────

export async function getSLAConfigByFund(fundId) {
  const q = query(collection(db, 'sla_config'), where('fund_id', '==', fundId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function saveSLAConfig({ fundId, stage, targetDays, performedBy }) {
  const q = query(
    collection(db, 'sla_config'),
    where('fund_id', '==', fundId),
    where('stage', '==', stage)
  );
  const snap = await getDocs(q);

  const data = {
    fund_id: fundId,
    stage,
    target_days: targetDays,
    created_by: performedBy,
    created_at: serverTimestamp(),
  };

  if (!snap.empty) {
    const existingId = snap.docs[0].id;
    await setDoc(doc(db, 'sla_config', existingId), { ...data, id: existingId });
  } else {
    const newDocRef = doc(collection(db, 'sla_config'));
    await setDoc(newDocRef, { ...data, id: newDocRef.id });
  }

  await writeAuditLog({
    action: 'sla_config_saved',
    entityType: 'system',
    entityId: `${fundId}_${stage}`,
    performedBy,
    details: { fundId, stage, targetDays },
  });
}

// ─── INSTRUMENT TYPES ─────────────────────────────────────────────────────────

export async function getAllInstrumentTypes() {
  const snap = await getDocs(collection(db, 'instrument_types'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getInstrumentTypesByFund(fundId) {
  const q = query(collection(db, 'instrument_types'), where('fund_id', '==', fundId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function saveInstrumentType({ instrumentId, instrumentData, performedBy }) {
  if (instrumentId) {
    const data = { ...instrumentData, id: instrumentId };
    await setDoc(doc(db, 'instrument_types', instrumentId), data);
    await writeAuditLog({
      action: 'instrument_type_saved',
      entityType: 'system',
      entityId: instrumentId,
      performedBy,
      details: { name: instrumentData.name },
    });
    return instrumentId;
  }

  const newDocRef = doc(collection(db, 'instrument_types'));
  const newData = {
    ...instrumentData,
    id: newDocRef.id,
    created_at: serverTimestamp(),
    created_by: performedBy,
  };
  await setDoc(newDocRef, newData);
  await writeAuditLog({
    action: 'instrument_type_saved',
    entityType: 'system',
    entityId: newDocRef.id,
    performedBy,
    details: { name: instrumentData.name },
  });
  return newDocRef.id;
}

// ─── IC MEMBERS ───────────────────────────────────────────────────────────────

export async function getICMembersByFund(fundId) {
  const q = query(collection(db, 'ic_members'), where('fund_id', '==', fundId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function saveICMember({ fundId, userId, memberData, performedBy }) {
  const memberId = `${fundId}_${userId}`;

  if (memberData.is_chair) {
    const existing = await getICMembersByFund(fundId);
    const currentChair = existing.find(m => m.is_chair && m.user_id !== userId && m.is_active);
    if (currentChair) {
      await updateDoc(doc(db, 'ic_members', currentChair.id), { is_chair: false });
    }
  }

  const data = {
    ...memberData,
    id: memberId,
    fund_id: fundId,
    user_id: userId,
    appointed_by: performedBy,
  };
  await setDoc(doc(db, 'ic_members', memberId), data);
  await writeAuditLog({
    action: 'ic_member_configured',
    entityType: 'system',
    entityId: memberId,
    performedBy,
    details: { fundId, userId, isChair: memberData.is_chair },
  });
  return memberId;
}

export async function deactivateICMember({ memberId, performedBy }) {
  await updateDoc(doc(db, 'ic_members', memberId), { is_active: false });
  await writeAuditLog({
    action: 'ic_member_deactivated',
    entityType: 'system',
    entityId: memberId,
    performedBy,
    details: {},
  });
}
